import { SegmentGroup } from '@ark-ui/react'
import { useMemo, useState } from 'react'
import type { BetRecord, BetRow } from '../api'
import { formatDay, formatTime, relativeTime } from '../time'
import { marketLabel } from '../markets'
import { Detail } from './Detail'
import { Term } from './Term'

/**
 * The betting record: what the rules suggested, and what it returned.
 *
 * Every row carries the explanation that produced it, including the rows where the answer was "no
 * bet". Showing only the bets would make the strategy look more decisive than it is — most matches
 * are correctly passed over, and that is the point of having a threshold.
 *
 * Open positions are kept visually and numerically apart from settled ones. Folding them together
 * would score games still being played as losses, which reads as a losing strategy for exactly as
 * long as it takes the results to arrive.
 */
/**
 * How a suggested bet stands, as ONE derivation.
 *
 * This exists because there were two, and they disagreed on screen. The summary card read the
 * server's `open` count and said "of 43 priced", meaning nothing was open; the simulator recomputed
 * openness as `won === null` and said "1 open position risking 15 kr" two hundred pixels below. Both
 * were describing the same map-3 bet on a Bo3 that finished 2–0 — a series that never reached that
 * map, so the stake came back.
 *
 * `voided` has to be tested FIRST. A void bet also has `won === null` and `profit === null`, so any
 * check that starts from those reads it as unfinished — which is how a returned stake came to be
 * displayed as a live position on a game that had already ended.
 */
export type BetStanding = 'void' | 'open' | 'settled' | 'passed'

// Exported for its own test. It is the single derivation the summary and the simulator share, and
// the bug it exists to prevent is invisible in a rendered page until a Bo3 finishes 2-0.
export function standing(bet: BetRow): BetStanding {
  if (!bet.recommended) return 'passed'
  if (bet.voided) return 'void'
  if (bet.won === null) return 'open'
  return 'settled'
}

export function BetLedger({ record }: { record: BetRecord }) {
  const { summary } = record
  const settled = summary.placed > 0

  // Normalised once, because the field is optional in the payload and `=== null` lets an absent
  // one through to a property access that throws. An older server simply does not send it.
  const lineMove = summary.lineMove ?? null

  const voided = record.bets.filter((bet) => standing(bet) === 'void')
  const open = record.bets.filter((bet) => standing(bet) === 'open')

  /*
   * The bankroll lives here rather than inside the simulator, because two things now need it.
   *
   * Every row's result used to read "+0.011" — thousandths of a bankroll, three decimals, two
   * hundred pixels below a box where the reader had already typed a figure in kroner. Nobody
   * evaluates a bet in millibankrolls. One number, entered once, denominates the whole page.
   */
  const [bankroll, setBankroll] = useState(1000)
  const [status, setStatus] = useState<Status>('all')

  const counts = {
    all: record.bets.length,
    open: record.bets.filter((bet) => standing(bet) === 'open').length,
    settled: record.bets.filter((bet) => standing(bet) === 'settled').length,
  }

  const groups = useMemo(() => groupByFixture(record.bets, status), [record.bets, status])

  return (
    <section className="space-y-4">
      {/*
        Ordered by what the reader came to find out, and five across so none is orphaned onto a row
        of its own.

        ROI LEADS. It and Profit are one result seen twice — profit-on-bankroll divided by the
        fraction staked IS the ROI, 34.9 / 72.3 = 48.2 — and printed side by side at equal weight,
        with no sign of that, the larger figure read as the achievement while answering a different
        question. ROI is the one that describes the strategy: profit as a share of the money actually
        risked, independent of how large a bankroll sat behind it. A million in the bank with a
        thousand at stake reports a rounding error whatever the bets did.
      */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat
          label="ROI"
          value={
            summary.roi === null
              ? '—'
              : `${summary.roi >= 0 ? '+' : ''}${(summary.roi * 100).toFixed(1)}%`
          }
          note="returned on every krone staked"
          tone={summary.roi === null ? 'flat' : summary.roi > 0 ? 'good' : 'bad'}
          detail={
            <>
              What the suggestions returned as a share of the money actually put at risk — the figure
              that describes the strategy rather than the size of the bankroll behind it.
              <span className="mt-2 block border-t border-line pt-2 text-ink-faint">
                Profit beside it is the same result measured against the whole bankroll, of which{' '}
                {(summary.stakedBankrolls * 100).toFixed(1)}% was ever staked. Divide one by the
                other and this number comes back.
              </span>
            </>
          }
        />
        <Stat
          label="Profit"
          value={`${summary.profitBankrolls >= 0 ? '+' : ''}${(summary.profitBankrolls * 100).toFixed(1)}%`}
          note={`of the whole bankroll · ${(summary.stakedBankrolls * 100).toFixed(1)}% of it staked`}
          tone={!settled ? 'flat' : summary.profitBankrolls > 0 ? 'good' : 'bad'}
        />
        {/*
          Then the evidence that converges fastest.

          Profit needs hundreds of settled bets to separate from luck; whether the market moves
          towards a pick is readable in dozens. Hit rate is demoted for the opposite reason — on a
          strategy that takes longshots almost exclusively it is the statistic most likely to
          mislead, high or low.

          It is deliberately NOT called closing line value. A suggestion here is struck at the
          closing price, so "did we beat the close" is zero by construction; what is measurable is
          whether the line came towards us after we picked a side.
        */}
        <Stat
          label="Market moved our way"
          value={
            lineMove === null
              ? '—'
              : `${lineMove.meanPoints >= 0 ? '+' : '−'}${Math.abs(
                  lineMove.meanPoints * 100,
                ).toFixed(1)} pts`
          }
          note={
            lineMove === null
              ? 'no market seen twice'
              : `${lineMove.withUs} of ${lineMove.measured} lines came towards us`
          }
          tone={
            lineMove === null || Math.abs(lineMove.meanPoints) < 0.005
              ? 'flat'
              : lineMove.meanPoints > 0
                ? 'good'
                : 'bad'
          }
          detail={
            <>
              After we pick a side, does the bookmaker&rsquo;s price move towards it or away? A
              market that drifts towards our pick is telling us our number carried information it
              had not yet priced — and that shows up in dozens of bets, where profit needs hundreds.
              <span className="mt-2 block border-t border-line pt-2 text-ink-faint">
                This is not the same as beating the closing line. Our suggestions are struck at the
                last price before kick-off, so there is no earlier price we took — that comparison
                would be zero by construction. Measured only on markets we captured more than once.
              </span>
            </>
          }
        />
        {/* Context, not headlines. On a strategy that takes longshots almost exclusively a hit rate
            is the figure most likely to mislead, in either direction. */}
        <Stat
          label="Hit rate"
          value={summary.hitRate === null ? '—' : `${(summary.hitRate * 100).toFixed(0)}%`}
          note={
            !settled
              ? 'nothing settled'
              : summary.placed < 30
                ? `${summary.wins} of ${summary.placed} — too few to judge`
                : `${summary.wins} of ${summary.placed} settled`
          }
        />
        <Stat
          label="Bets suggested"
          value={`${summary.placed + open.length}`}
          note={
            open.length > 0
              ? `${open.length} still open`
              : voided.length > 0
                ? `${voided.length} void, of ${summary.considered} priced`
                : `of ${summary.considered} priced`
          }
        />
      </div>

      {/*
        Two amber bars used to sit here, one above these figures and one below, and between them
        they said the same thing the Beta badge in the header now says once. What is left of the
        sample-size caveat has moved INTO the figures it qualifies — a note under "Hit rate" that
        travels with the number, rather than a banner the reader scrolls past to reach it.
      */}

      {!settled && open.length > 0 ? (
        <p className="rounded border border-accent-edge bg-accent-bg px-3 py-2 text-xs text-ink-soft">
          {open.length} {open.length === 1 ? 'suggestion is' : 'suggestions are'} still open. Nothing
          is scored until the series resolves, so profit stays at zero rather than showing a loss for
          games that have not finished.
        </p>
      ) : null}

      <Simulator record={record} bankroll={bankroll} onBankroll={setBankroll} />

      {/*
        TWO LAYOUTS, because five columns do not fit a phone.

        The table needs 544px before it will compress and an iPhone gives it 393. It was inside an
        `overflow-x-auto`, which meant Odds, Edge and Result sat off the right edge with no fade, no
        scrollbar and no hint they existed — so the phone showed the two least useful columns, which
        match and which side, and hid every number the page is published to provide. It passed the
        layout check too: content inside a declared horizontal scroller is treated as intentional,
        and here it was not.

        So below `sm` the same facts stack into a card. Nothing is dropped and nothing is hidden
        behind a gesture.
      */}
      <StatusFilter value={status} onChange={setStatus} counts={counts} />

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-ink-faint">
              <th className="py-2 pr-3 font-medium">Bet</th>
              <th className="py-2 pr-3 text-right font-medium">Odds</th>
              <th className="py-2 pr-3 text-right font-medium">Edge</th>
              <th className="py-2 text-right font-medium">Result</th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.fixtureId}>
              {group.newDay ? (
                <tr>
                  <th
                    colSpan={4}
                    className="pt-5 pb-1 text-left text-xs font-medium text-ink-faint"
                  >
                    {formatDay(group.startTime)}
                  </th>
                </tr>
              ) : null}
              <tr>
                <th colSpan={4} className="border-b border-line/50 pt-3 pb-1 text-left font-normal">
                  <FixtureHeading bets={group.bets} />
                </th>
              </tr>
              {group.bets.map((bet) => (
                <Row key={bet.market} bet={bet} bankroll={bankroll} />
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <ul className="space-y-3 sm:hidden">
        {groups.map((group) => (
          <li key={group.fixtureId}>
            {group.newDay ? (
              <p className="mb-2 mt-4 text-xs font-medium text-ink-faint first:mt-0">
                {formatDay(group.startTime)}
              </p>
            ) : null}
            <div className="rounded-lg border border-line bg-surface p-3">
              <FixtureHeading bets={group.bets} />
              <ul className="mt-2 divide-y divide-line">
                {group.bets.map((bet, index) => (
                  <MarketCard
                    key={bet.market}
                    bet={bet}
                    bankroll={bankroll}
                    labelled={index === 0}
                  />
                ))}
              </ul>
            </div>
          </li>
        ))}
      </ul>

      {groups.length === 0 && record.bets.length > 0 ? (
        <p className="rounded border border-line bg-surface px-4 py-6 text-center text-sm text-ink-soft">
          Nothing matches that filter.
        </p>
      ) : null}

      {record.bets.length === 0 ? (
        <p className="rounded border border-line bg-surface px-4 py-6 text-center text-sm text-ink-soft">
          No priced matches yet. Bookmaker odds are withdrawn the moment a match starts and are
          archived nowhere, so they have to be captured beforehand — the record fills as the
          tournament runs.
        </p>
      ) : null}
    </section>
  )
}

type Status = 'all' | 'open' | 'settled'

interface FixtureGroup {
  fixtureId: number
  startTime: number
  bets: BetRow[]
  /** First group of its local day, so the list can print a date above it. */
  newDay: boolean
}

/**
 * One entry per match, not one per market.
 *
 * The record was a flat list in which a three-market fixture appeared three times, each row
 * repeating the same teams, the same kick-off and the same score — 43 rows for 15 matches. The card
 * layout made it plainer still: three full cards headed "BoomBoys v TEAM VISION · 07:00 · 0–2",
 * stacked. The reader's unit is the match; the market is a way of betting it.
 *
 * Server order is preserved rather than re-sorted. It arrives newest-first, which puts anything
 * unstarted at the top — the only rows still worth acting on, and what the page copy promises.
 */
function groupByFixture(bets: BetRow[], status: Status): FixtureGroup[] {
  const keep =
    status === 'all' ? bets : bets.filter((bet) => standing(bet) === status)

  const groups: FixtureGroup[] = []
  const byId = new Map<number, FixtureGroup>()

  for (const bet of keep) {
    const existing = byId.get(bet.fixtureId)
    if (existing) {
      existing.bets.push(bet)
      continue
    }
    const group: FixtureGroup = {
      fixtureId: bet.fixtureId,
      startTime: bet.startTime,
      bets: [bet],
      newDay: false,
    }
    byId.set(bet.fixtureId, group)
    groups.push(group)
  }

  // A date above the first fixture of each day. Times alone were ambiguous across a fortnight of
  // matches that all kick off in the same few hours.
  let previousDay = ''
  for (const group of groups) {
    const day = new Date(group.startTime * 1000).toDateString()
    group.newDay = day !== previousDay
    previousDay = day
  }

  return groups
}

/** Teams, kick-off and score, printed once per match instead of once per market. */
function FixtureHeading({ bets }: { bets: BetRow[] }) {
  const [first] = bets
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-sm text-ink">
        {first.teamA} <span className="text-ink-faint">v</span> {first.teamB}
      </span>
      <span className="tabular text-xs text-ink-faint">{formatTime(first.startTime)}</span>
      {first.isLive ? (
        <span className="flex items-center gap-1 text-xs font-medium text-live">
          <span className="size-1.5 animate-pulse rounded-full bg-live motion-reduce:animate-none" />
          LIVE
        </span>
      ) : first.startsInSeconds > 0 ? (
        <span className="text-xs text-accent">{relativeTime(first.startTime)} — still open</span>
      ) : null}
      {first.resolved && first.scoreA + first.scoreB > 0 ? (
        <span className="tabular text-xs text-ink-faint">
          {first.scoreA}–{first.scoreB}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Priced / still open / settled.
 *
 * Three questions rather than a general query builder, because those are the three a reader of this
 * page actually has, and the first — what can I still act on — was previously answerable only by
 * reading every row looking for the word "open".
 *
 * The first was labelled "All", which read as a contradiction beside the other two: "All 12" next
 * to "Still open 0" and "Settled 0" invites the reader to work out where the other twelve went.
 * They are markets a price was captured for and no bet was advised on — which is what "priced"
 * means, and what the summary card directly above already calls them.
 */
function StatusFilter({
  value,
  onChange,
  counts,
}: {
  value: Status
  onChange: (next: Status) => void
  counts: Record<Status, number>
}) {
  const options: { value: Status; label: string }[] = [
    { value: 'all', label: 'Priced' },
    { value: 'open', label: 'Still open' },
    { value: 'settled', label: 'Settled' },
  ]

  return (
    <SegmentGroup.Root
      value={value}
      onValueChange={(details) => details.value && onChange(details.value as Status)}
      aria-label="Filter bets"
      className="inline-flex overflow-hidden rounded-lg border border-line text-sm"
    >
      {options.map((option) => (
        <SegmentGroup.Item
          key={option.value}
          value={option.value}
          disabled={counts[option.value] === 0 && option.value !== 'all'}
          className="relative flex cursor-pointer items-center gap-1.5 border-l border-line px-3 py-1.5 leading-none text-ink-soft transition-colors first:border-l-0 hover:bg-surface-raised hover:text-ink data-[disabled]:cursor-default data-[disabled]:text-ink-faint data-[disabled]:hover:bg-transparent data-[state=checked]:bg-accent-bg data-[state=checked]:text-accent"
        >
          <SegmentGroup.ItemText>{option.label}</SegmentGroup.ItemText>
          <span className="tabular text-xs text-ink-faint">{counts[option.value]}</span>
          <SegmentGroup.ItemHiddenInput className="absolute opacity-0" />
        </SegmentGroup.Item>
      ))}
    </SegmentGroup.Root>
  )
}

/**
 * Is the sharp line far enough from the book being bet to be worth a reader's attention?
 *
 * Ten points. Below it two books are disagreeing by less than the noise in either line, which is
 * ordinary and not worth an amber note on every row. Above it the likeliest explanation is not that
 * one book is wrong but that something at our end is — a mismatched fixture, a stale price, a side
 * resolved the wrong way round. That is the case worth surfacing.
 */
const WIDE_SHARP_GAP = 0.1

const isWideGap = (edge: number) => Math.abs(edge) >= WIDE_SHARP_GAP

/** Which market, which side, and — where there is no bet — why not. */
function Pick({ bet }: { bet: BetRow }) {
  return (
    <>
      <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs whitespace-nowrap">
        <Term id={bet.market === 'series' ? 'series-market' : 'map-market'}>
          {marketLabel(bet.market)}
        </Term>
      </span>{' '}
      {bet.recommended ? (
        <span className="font-medium text-ink">{bet.side}</span>
      ) : (
        <Detail label="Why there is no bet here" className="text-ink-faint" heading={<span>no bet</span>}>
          {bet.reason}
        </Detail>
      )}
      {/*
        The sharp line INFORMS a row now; it no longer suppresses one.

        It used to veto: a suggestion the sharp book contradicted was never staked. That measured a
        hybrid of our model and Pinnacle's rather than ours, and it was justified by the
        under-confidence finding, which has since been retracted. Every suggestion the edge rule
        makes is now published as made.

        What is worth keeping is the WIDE disagreement, and only that. Under ten points two books
        are inside each other's noise. Past it the likeliest explanation is not a difference of
        opinion but a fault at our end — a mismatched fixture, a stale price, a side resolved the
        wrong way round — which is the one case a reader benefits from seeing.
      */}
      {bet.sharp && isWideGap(bet.sharp.edge) ? (
        <div className="text-xs text-warn">
          <Detail
            label="What the sharp book said"
            className="text-warn"
            heading={<span>worth checking</span>}
          >
            <strong className="text-ink">{bet.sharp.bookmakerName}</strong> prices this side at{' '}
            <span className="tabular text-ink">{bet.sharp.odds.toFixed(2)}</span>, rating it{' '}
            {Math.abs(bet.sharp.edge * 100).toFixed(1)} points{' '}
            {bet.sharp.edge > 0 ? 'higher' : 'lower'} than the book being bet. A gap that wide
            between two books pricing the same match is more often a fault at our end than a real
            disagreement.
          </Detail>{' '}
          — <Term id="sharp-book">sharp book</Term> is{' '}
          {Math.abs(bet.sharp.edge * 100).toFixed(0)} points away
        </div>
      ) : null}
    </>
  )
}

/** The stake, in the same money as everything else. */
function Stake({ bet, bankroll }: { bet: BetRow; bankroll: number }) {
  if (!bet.recommended) return null
  return (
    <div className="tabular text-xs text-ink-faint">
      {money(bet.stakeFraction * bankroll)} · {(bet.stakeFraction * 100).toFixed(2)}%{' '}
      <span className="font-sans">
        <Term id="stake">stake</Term>
      </span>
    </div>
  )
}

/**
 * Our number against the one the price implies, printed rather than hidden in a tooltip.
 *
 * Against the price, margin and all, because that is what the stake is paid at. The second line
 * used to read our figure against the DE-VIGGED one, which is a different and larger gap: the book's
 * private opinion rather than its offer. Both are real, and quoting the wrong one as "edge" is what
 * put a highlighted 1.31 beside a card saying the bet needed 1.36.
 */
function EdgeFigure({ bet }: { bet: BetRow }) {
  return (
    <>
      <div>
        {bet.edge >= 0 ? '+' : ''}
        {(bet.edge * 100).toFixed(1)}
      </div>
      <div className="text-xs text-ink-faint">
        {(bet.modelProbability * 100).toFixed(0)}% v {(bet.impliedProbability * 100).toFixed(0)}%
      </div>
    </>
  )
}

/**
 * One market of one match, on a phone.
 *
 * The match itself is named once by the group heading above, so this carries only what distinguishes
 * this row from its siblings: which market and side, and what it did. The outcome is a labelled
 * three-up so the numbers keep the column alignment that makes them scannable, without a table.
 */
function MarketCard({
  bet,
  bankroll,
  labelled,
}: {
  bet: BetRow
  bankroll: number
  /** Only the first market of a match names its columns; the rest align under them. */
  labelled: boolean
}) {
  return (
    <li className={`py-2 first:pt-0 last:pb-0 ${bet.recommended ? '' : 'text-ink-faint'}`}>
      <div className="text-sm">
        <Pick bet={bet} />
        <Stake bet={bet} bankroll={bankroll} />
      </div>

      <dl className="mt-1.5 grid grid-cols-3 gap-2 text-sm">
        <div className="min-w-0">
          {labelled ? (
            <dt className="text-xs text-ink-faint">
              <Term id="odds" />
            </dt>
          ) : null}
          <dd className="tabular mt-0.5">{bet.decimalOdds.toFixed(2)}</dd>
        </div>
        <div className="min-w-0">
          {labelled ? (
            <dt className="text-xs text-ink-faint">
              <Term id="edge" />
            </dt>
          ) : null}
          <dd className={`tabular mt-0.5 ${bet.edge > 0 ? 'text-win' : 'text-ink-faint'}`}>
            <EdgeFigure bet={bet} />
          </dd>
        </div>
        <div className="min-w-0 text-right">
          {labelled ? <dt className="text-xs text-ink-faint">Result</dt> : null}
          <dd className="tabular mt-0.5">
            <Result bet={bet} bankroll={bankroll} />
          </dd>
        </div>
      </dl>
    </li>
  )
}

function Row({ bet, bankroll }: { bet: BetRow; bankroll: number }) {
  return (
    <tr className={`border-b border-line/50 ${bet.recommended ? '' : 'text-ink-faint'}`}>
      <td className="py-2 pr-3">
        <Pick bet={bet} />
        {/* The staking rule's answer, kept but demoted. It explains the result column without
            competing with the decision the reader is actually reading. */}
        <Stake bet={bet} bankroll={bankroll} />
      </td>
      <td className="tabular py-2 pr-3 text-right">{bet.decimalOdds.toFixed(2)}</td>
      {/* The subtraction is PRINTED under its own answer, not hidden in a tooltip. It is four
          characters wide and it is the whole basis of the row; a reader should not have to own a
          mouse to see where "+10.1" came from. */}
      <td className={`tabular py-2 pr-3 text-right ${bet.edge > 0 ? 'text-win' : 'text-ink-faint'}`}>
        <EdgeFigure bet={bet} />
      </td>
      <td className="tabular py-2 text-right">
        <Result bet={bet} bankroll={bankroll} />
      </td>
    </tr>
  )
}

/**
 * The outcome cell, which has four states rather than two — and speaks in money.
 *
 * "Open" is not "—": a suggestion awaiting a result is a live position and the reader should be able
 * to see that it exists. A passed-over match genuinely has nothing to report. And a VOID bet is
 * neither — the series ended before that map was played, so the stake came back. Scoring it as a
 * loss invents a loss; leaving it open, which is what this cell used to do, claims a position on a
 * game that has already finished.
 *
 * The unit used to be bankroll fractions to three decimals: `+0.011`, `-0.020`. Two problems with
 * that. Nobody reasons in thousandths of a bankroll, and the column did not actually say whether the
 * bet WON — only a sign and a colour carried it, and colour is doing enough work already. Now it
 * says both, in the currency the reader typed in above.
 */
function Result({ bet, bankroll }: { bet: BetRow; bankroll: number }) {
  switch (standing(bet)) {
    case 'passed':
      return <span className="text-ink-faint">—</span>

    case 'void':
      return (
        <div>
          <span className="rounded bg-void-bg px-1.5 py-0.5 text-xs uppercase tracking-wide text-void">
            void
          </span>
          <div className="text-xs text-ink-faint">stake back</div>
        </div>
      )

    case 'open':
      return (
        <div>
          <span className="text-pending">open</span>
          <div className="tabular text-xs text-ink-faint">
            {money(bet.stakeFraction * bankroll)} at risk
          </div>
        </div>
      )

    default: {
      const returned = bet.profit! * bankroll
      return (
        <div className={bet.won ? 'text-win' : 'text-loss'}>
          <div>{bet.won ? 'won' : 'lost'}</div>
          <div className="tabular text-xs">
            {returned >= 0 ? '+' : '−'}
            {money(Math.abs(returned))}
          </div>
        </div>
      )
    }
  }
}

function Stat({
  label,
  value,
  note,
  tone = 'flat',
  detail,
}: {
  label: string
  value: string
  note?: string
  tone?: 'good' | 'bad' | 'flat'
  /** What the figure means, for one that cannot be read off its own label. */
  detail?: React.ReactNode
}) {
  const colour = tone === 'good' ? 'text-win' : tone === 'bad' ? 'text-loss' : 'text-ink'

  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="text-xs text-ink-faint">
        {detail ? (
          <Detail label={`${label} — what is this?`} heading={<span>{label}</span>}>
            {detail}
          </Detail>
        ) : (
          label
        )}
      </div>
      <div className={`tabular mt-1 text-xl ${colour}`}>{value}</div>
      {note ? <div className="mt-0.5 text-xs text-ink-faint">{note}</div> : null}
    </div>
  )
}

/**
 * What the suggestions would have done to a real bankroll.
 *
 * Percentages are the right unit for reasoning and the wrong one for grounding: "0.008 bankrolls"
 * is not a quantity anyone can picture. A cash figure against a stake you choose can be judged, and
 * choosing it yourself is the point — a fixed default would read as a recommendation.
 *
 * It shows the arithmetic rather than a total. A profit figure with no visible bet sizes behind it
 * asks to be trusted, which is the opposite of what this page is for, so every settled bet is
 * listed with what it staked and what it returned.
 *
 * The two columns exist because the sharp-book filter is a hypothesis, not a settled answer.
 * Showing what it saved or cost is the only way to find out whether it earns its place.
 */
function Simulator({
  record,
  bankroll,
  onBankroll,
}: {
  record: BetRecord
  bankroll: number
  onBankroll: (next: number) => void
}) {
  // `won !== null` already excludes a void, but say so rather than rely on it: a returned stake is
  // not a settled bet and must not reach the profit arithmetic from either direction.
  const settledAll = record.bets.filter((b) => b.shouldBet && !b.voided && b.won !== null)
  const settledKept = settledAll.filter((b) => b.recommended)
  const open = record.bets.filter((b) => standing(b) === 'open')
  const voided = record.bets.filter((b) => standing(b) === 'void')

  // Recomputed from odds rather than read off the row: a held-back bet reports zero profit by
  // design, and the "ignored the filter" column needs to know what it would have returned.
  const settle = (b: BetRow) => (b.won ? b.stakeFraction * (b.decimalOdds - 1) : -b.stakeFraction)

  const summarise = (bets: BetRow[]) => ({
    count: bets.length,
    staked: bets.reduce((sum, b) => sum + b.stakeFraction, 0) * bankroll,
    profit: bets.reduce((sum, b) => sum + settle(b), 0) * bankroll,
    wins: bets.filter((b) => b.won).length,
  })

  // `settledKept` and `settledAll` are now the same set — nothing is held back — but both names are
  // kept so the distinction is easy to reintroduce if a filter ever earns its place again.
  const kept = summarise(settledKept)

  const atRisk = open.reduce((sum, b) => sum + b.stakeFraction, 0) * bankroll

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-medium">If you had followed every suggestion</h3>
        <label className="flex items-center gap-2 text-xs text-ink-faint">
          Bankroll
          <input
            type="number"
            min={0}
            step={100}
            value={bankroll}
            onChange={(e) => onBankroll(Math.max(0, Number(e.target.value) || 0))}
            className="tabular w-28 rounded border border-line bg-surface-raised px-2 py-1 text-right text-sm text-ink"
          />
          <span>kr</span>
        </label>
      </div>

      {settledAll.length === 0 ? (
        <p className="mt-3 text-sm text-ink-soft">
          Nothing has settled yet.{' '}
          {open.length > 0
            ? `${open.length} ${open.length === 1 ? 'suggestion is' : 'suggestions are'} open, risking ${money(atRisk)}.`
            : 'No suggestions are open either.'}
        </p>
      ) : (
        <>
          {/*
            One outcome, because there is now only one.

            This was two panels — "Following the sharp-book filter" and "Ignoring it" — from when a
            sharp disagreement suppressed a bet. Nothing is suppressed any more: every suggestion the
            edge rule makes is staked in the record, so the two columns would print the same number
            twice.
          */}
          <div className="mt-3">
            <Outcome
              title="What these suggestions returned"
              note={`${settledKept.length} settled at ${record.bookmakerName}`}
              summary={kept}
              bankroll={bankroll}
              highlight
            />
          </div>

          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-ink-faint hover:text-ink-soft">
              Show the {settledAll.length} settled {settledAll.length === 1 ? 'bet' : 'bets'} behind
              these numbers
            </summary>
            {/* Same two-layout rule as the ledger above, and for the same reason: five columns in
                480px do not fit a 309px card column, and a table that hides "Returned" is showing
                the reader everything except the answer. */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="mt-2 w-full min-w-[30rem] text-xs">
                <thead>
                  <tr className="border-b border-line text-left text-ink-faint">
                    <th className="py-1 pr-2 font-medium">Pick</th>
                    <th className="py-1 pr-2 text-right font-medium">Odds</th>
                    <th className="py-1 pr-2 text-right font-medium">Staked</th>
                    <th className="py-1 text-right font-medium">Returned</th>
                  </tr>
                </thead>
                <tbody>
                  {settledAll.map((b) => {
                    const returned = settle(b) * bankroll
                    return (
                      <tr key={`${b.fixtureId}-${b.market}`} className="border-b border-line/40">
                        <td className="py-1 pr-2">
                          {b.side}{' '}
                          <span className="text-ink-faint">{marketLabel(b.market).toLowerCase()}</span>
                        </td>
                        <td className="tabular py-1 pr-2 text-right">{b.decimalOdds.toFixed(2)}</td>
                        <td className="tabular py-1 pr-2 text-right">
                          {money(b.stakeFraction * bankroll)}
                        </td>
                        <td
                          className={`tabular py-1 text-right ${
                            returned >= 0 ? 'text-win' : 'text-loss'
                          }`}
                        >
                          {returned >= 0 ? '+' : ''}
                          {money(returned)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <ul className="mt-2 space-y-1.5 text-xs sm:hidden">
              {settledAll.map((b) => {
                const returned = settle(b) * bankroll
                return (
                  <li
                    key={`${b.fixtureId}-${b.market}`}
                    className="flex flex-wrap items-baseline justify-between gap-x-2 border-b border-line/40 pb-1.5"
                  >
                    <span className="min-w-0">
                      {b.side}{' '}
                      <span className="text-ink-faint">{marketLabel(b.market).toLowerCase()}</span>
                    </span>
                    <span className="tabular shrink-0 whitespace-nowrap">
                      <span className="text-ink-faint">
                        {b.decimalOdds.toFixed(2)} · {money(b.stakeFraction * bankroll)} →{' '}
                      </span>
                      <span className={returned >= 0 ? 'text-win' : 'text-loss'}>
                        {returned >= 0 ? '+' : ''}
                        {money(returned)}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          </details>

          {open.length > 0 ? (
            <p className="mt-3 text-xs text-ink-faint">
              {open.length} open {open.length === 1 ? 'position' : 'positions'} risking{' '}
              {money(atRisk)}, not counted above — an unfinished game has no result to score.
            </p>
          ) : null}

          {voided.length > 0 ? (
            <p className="mt-2 text-xs text-ink-faint">
              {voided.length} {voided.length === 1 ? 'bet was' : 'bets were'} voided — the series
              ended before that map was played, so the stake came back. Not counted above either way:
              a returned stake is neither a win nor a loss.
            </p>
          ) : null}
        </>
      )}

      <p className="mt-3 border-t border-line pt-3 text-xs text-ink-faint">
        Stakes are quarter-<Term id="kelly" />, capped at 2% of bankroll per bet — so at{' '}
        {money(bankroll)} the largest single bet is {money(bankroll * 0.02)}. These are the rules
        applied to prices captured before each match, not a record of bets anyone placed.
      </p>
    </section>
  )
}

function Outcome({
  title,
  note,
  summary,
  bankroll,
  highlight = false,
}: {
  title: string
  note: string
  summary: { count: number; staked: number; profit: number; wins: number }
  bankroll: number
  highlight?: boolean
}) {
  const tone = summary.profit > 0 ? 'text-win' : summary.profit < 0 ? 'text-loss' : 'text-ink'
  return (
    <div className={`rounded border p-3 ${highlight ? 'border-accent-edge bg-accent-bg' : 'border-line'}`}>
      <div className="text-xs font-medium text-ink">{title}</div>
      <div className="text-xs text-ink-faint">{note}</div>
      <div className={`tabular mt-2 text-xl ${tone}`}>
        {summary.profit >= 0 ? '+' : ''}
        {money(summary.profit)}
      </div>
      <div className="mt-1 text-xs text-ink-faint">
        {summary.wins}/{summary.count} won · {money(summary.staked)} staked · bankroll{' '}
        {money(bankroll + summary.profit)}
      </div>
    </div>
  )
}

const money = (amount: number) =>
  `${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} kr`
